import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { AlertTriangle, ChevronRight, Radar } from 'lucide-react';
import type { TFunction } from 'i18next';
import type { DisruptionCaseRow, DisruptionOverviewRow } from '@/lib/disruptions/types';
import { cn } from '@/lib/cn';
import { Skeleton } from '@/components/ui/skeleton';
import { timeAgo } from './labels';
import { rowAirportCodes } from './situation';
import { disruptionEntry } from './disruption-entry';
import { FlightWhen } from './flight-when';
import { towerPath } from './operar/operate-types';
import { ACTION_TONE, type ActionTone } from './operar/views/cancelled/action-tone';
import {
  BOARD_BADGE_TONE,
  boardFlights,
  isOpen,
  rowBadge,
  rowFigure,
  type IBoardFlight,
} from './board-model';

// The rows of the disruptions list, in the philosophy of «Operar la disrupción»: what
// matters, big, nothing else. Each row says, in one glance:
//
//   - WHICH flights (the title: code big in mono + route when the detail is cached;
//     the codes of the scope otherwise; the disruption's title when neither knows one)
//     and WHEN — each flight's scheduled departure, its day in words («Hoy 14:35»,
//     flight-when.tsx; PO 2026-11-01);
//   - in WHAT state — ONE badge, Operar's words and tones (`rowBadge`);
//   - HOW MANY passengers — the one big number (`rowFigure`): «sin resolver» on a
//     cancellation; on a plain delay just «pasajeros» (they keep their flight, nobody
//     is unresolved); «sin dato» while the counts have not landed; none once closed;
//   - SINCE WHEN («hace 31 min»);
//   - and, only when it is so, that a provider report over the policy maximum WAITS for
//     the operator's decision (`flightStatus.outOfPolicy` — the one flag of the list
//     that asks the operator to act; Operar shows the same «espera tu decisión»).
//
// Gone on purpose: the spend, the case count, the cause chip, the staleness chip and the
// rebooked bar. The bar said the same as the big number (unresolved = affected − rebooked)
// in a weaker way — a fraction of a bar does not tell 3 passengers from 300.
//
// The row opens Operar (`disruptionEntry`). The Tower stays one quiet icon away, a
// SIBLING of the row's link (a link inside a link is invalid); it needs no gate of its
// own: the route asks the same `NAV.disruptions` permission as this list.
//
// The LOOK is Operar's lanes and the /express board: rows of one bordered card, divided,
// on a FIXED column grid (`BOARD_GRID`) so every badge, every number and every action
// lines up down the list — the board's column header (`BoardColumns`) sits on the same
// grid. The right column says what the row is to the operator in Operar's action tones
// (`action-tone.ts`): «Operar» filled Nexa blue on the row the navy card points at
// (ringed in the Nexa cyan, as the lane «Lo que sigue» names), «Decidir» in rose while a
// report waits for the operator, a quiet «Operar» otherwise. It is a label of the row's
// link, not a button of its own.

/** `main` — today's rows, big; `compact` — yesterday's; `quiet` — the muted «Sin movimiento». */
export type QueueVariant = 'main' | 'compact' | 'quiet';

/** The board's columns: flights · state · passengers · action. One template for the
 *  rows and their header, so they line up; stacked under `md` (§18.2). */
const BOARD_GRID =
  'grid gap-x-4 gap-y-2 md:grid-cols-[minmax(0,1fr)_11.5rem_8.5rem_7.5rem] md:items-center';

/** The row's side padding — the header repeats it so the columns stay aligned. */
const ROW_PAD = 'px-3';

/** The column header of the board — mono, tiny, on the muted strip of /express. */
export function BoardColumns() {
  const { t } = useTranslation('disruptions');
  return (
    <div
      aria-hidden
      className="hidden shrink-0 items-center gap-1 border-y border-border bg-muted/40 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground md:flex"
      data-testid="board-columns"
    >
      <div className={cn(BOARD_GRID, ROW_PAD, 'min-w-0 flex-1')}>
        <span>{t('board.columns.flight')}</span>
        <span>{t('board.columns.state')}</span>
        <span className="text-right">{t('board.columns.passengers')}</span>
        <span />
      </div>
      {/* the Tower icon's column */}
      <span className="w-11 shrink-0" />
    </div>
  );
}

/** How many flights the title names before «+N vuelos». */
const MAX_TITLE_FLIGHTS = 3;

export function PriorityQueue({
  rows,
  variant = 'main',
  casesByUrn,
  pendingUrns,
  nextUrn,
  testId = 'priority-queue',
}: {
  rows: DisruptionOverviewRow[];
  variant?: QueueVariant;
  /** The cached detail's cases per disruption URN — the route of each flight. */
  casesByUrn?: ReadonlyMap<string, readonly DisruptionCaseRow[]>;
  /** Disruptions whose detail is being read right now — their title waits for it. */
  pendingUrns?: ReadonlySet<string>;
  /** The row the board's navy card points at («Lo que sigue») — ringed, filled action. */
  nextUrn?: string | null;
  testId?: string;
}) {
  return (
    <ul className="divide-y divide-border/60" data-testid={testId}>
      {rows.map((row) => (
        <QueueRow
          key={row.urn}
          row={row}
          variant={variant}
          flights={boardFlights(row, casesByUrn?.get(row.urn))}
          pending={pendingUrns?.has(row.urn) ?? false}
          next={row.urn === nextUrn}
        />
      ))}
    </ul>
  );
}

/**
 * The place under the title — only when the title has no route to say it: the scope's
 * airports, «Toda la red» for a genuinely network-wide one (no airports, no flights and
 * several cases — docs/kpi-definitions.md §3.1, us3-group/nexa#746), else nothing.
 */
function placeOf(
  row: DisruptionOverviewRow,
  flights: readonly IBoardFlight[],
  t: TFunction<'disruptions'>,
): string {
  if (flights.some((f) => f.origin && f.destination)) return '';
  const airports = rowAirportCodes(row);
  if (airports.length > 0) return airports.join(' · ');
  if (flights.length === 0 && row.rollup.caseCount > 1) return t('board.queue.networkWide');
  return '';
}

function FlightsTitle({
  row,
  flights,
  variant,
  pending,
}: {
  row: DisruptionOverviewRow;
  flights: readonly IBoardFlight[];
  variant: QueueVariant;
  /** The detail (the flights) is being read: hold the title's place instead of showing
   *  the disruption's title and then jumping to the flight codes. */
  pending: boolean;
}) {
  const { t } = useTranslation('disruptions');
  if (flights.length === 0 && pending) {
    return (
      <Skeleton
        aria-hidden
        className={cn('w-32 max-w-full', variant === 'main' ? 'h-7' : 'h-6')}
        data-testid="queue-title-pending"
      />
    );
  }
  if (flights.length === 0) {
    return (
      <span
        className={cn('min-w-0 truncate font-semibold', variant === 'main' ? 'text-lg' : 'text-base')}
        data-testid="queue-title"
      >
        {row.title}
      </span>
    );
  }
  const shown = flights.slice(0, MAX_TITLE_FLIGHTS);
  const more = flights.length - shown.length;
  return (
    <span
      className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5"
      data-testid="queue-title"
    >
      {shown.map((f) => (
        <span key={`${f.code}|${f.origin ?? ''}|${f.destination ?? ''}`} className="flex items-baseline gap-1.5">
          <span
            className={cn(
              'font-mono font-bold tracking-tight',
              variant === 'main' ? 'text-xl' : 'text-base',
            )}
          >
            {f.code}
          </span>
          {f.origin && f.destination ? (
            <span className="text-sm text-muted-foreground">
              {t('board.queue.route', { origin: f.origin, destination: f.destination })}
            </span>
          ) : null}
          {f.departureAt ? (
            <FlightWhen
              departureAt={f.departureAt}
              origin={f.origin}
              className="ml-1 text-sm font-medium text-foreground/80"
            />
          ) : null}
        </span>
      ))}
      {more > 0 ? (
        <span className="text-sm text-muted-foreground">
          {t('board.queue.moreFlights', { count: more })}
        </span>
      ) : null}
    </span>
  );
}

function QueueRow({
  row,
  variant,
  flights,
  pending,
  next,
}: {
  row: DisruptionOverviewRow;
  variant: QueueVariant;
  flights: IBoardFlight[];
  pending: boolean;
  /** The row «Lo que sigue» names. */
  next: boolean;
}) {
  const { t } = useTranslation('disruptions');
  const badge = rowBadge(row);
  const figure = rowFigure(row);
  const open = isOpen(row);
  const decision = open && !!row.flightStatus?.outOfPolicy;
  const readable = Number.isFinite(Date.parse(row.detectedAt));
  const age = readable
    ? timeAgo(row.detectedAt, t) || t('timeAgo.justNow')
    : t('board.queue.noData');
  const place = placeOf(row, flights, t);
  const entry = disruptionEntry(row.urn);
  const main = variant === 'main';
  const quiet = variant === 'quiet';
  const tone: ActionTone = next ? 'next' : decision ? 'alert' : 'quiet';
  return (
    <li className="flex items-center gap-1 px-2 py-1">
      <Link
        to={entry.to}
        state={entry.state}
        title={row.title}
        data-testid={`queue-card-${row.urn}`}
        data-variant={variant}
        data-next={next || undefined}
        className={cn(
          BOARD_GRID,
          ROW_PAD,
          'min-h-11 min-w-0 flex-1 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          main ? 'py-3' : 'py-2',
          next
            ? 'bg-nexa-cyan/5 ring-2 ring-inset ring-nexa-cyan/60 hover:bg-nexa-cyan/10'
            : 'hover:bg-muted/50',
          quiet && 'text-muted-foreground',
        )}
      >
        <div className="min-w-0">
          <FlightsTitle row={row} flights={flights} variant={variant} pending={pending} />
          <p className="mt-0.5 truncate text-sm text-muted-foreground" data-testid="queue-meta">
            {[place, age].filter(Boolean).join(' · ')}
          </p>
          {decision ? (
            <p
              className="mt-1 flex items-center gap-1.5 text-sm font-medium text-rose-700 dark:text-rose-400"
              data-testid="queue-decision"
            >
              <AlertTriangle aria-hidden className="h-4 w-4 shrink-0" />
              {t('board.queue.decision')}
            </p>
          ) : null}
        </div>
        <div className="min-w-0">
          <span
            className={cn(
              'inline-block max-w-full truncate rounded-md px-2 py-1 text-xs font-bold uppercase tracking-wide',
              BOARD_BADGE_TONE[badge.tone],
            )}
            data-testid="queue-badge"
            data-tone={badge.tone}
          >
            {t(badge.key, badge.params ?? {})}
          </span>
        </div>
        <div className="flex items-baseline gap-2 md:flex-col md:items-end md:gap-0 md:text-right">
          {figure === null ? null : (
            <div
              className="flex items-baseline gap-2 md:flex-col md:items-end md:gap-0"
              data-testid="queue-figure"
              data-kind={figure.kind}
            >
              {figure.n === null ? (
                <span className="text-sm font-medium text-muted-foreground">
                  {t('board.queue.noData')}
                </span>
              ) : (
                <span
                  className={cn(
                    'font-mono font-bold leading-none tabular-nums',
                    main ? 'text-3xl' : 'text-xl',
                    figure.kind === 'unresolved' && figure.n > 0 && !quiet
                      ? 'text-foreground'
                      : 'text-muted-foreground',
                  )}
                >
                  {figure.n.toLocaleString()}
                </span>
              )}
              {/* `count` only picks the plural; «sin dato» reads with the plural label. */}
              <span className="text-xs leading-tight text-muted-foreground md:mt-1 md:max-w-32">
                {t(`board.queue.${figure.kind}`, { count: figure.n ?? 0 })}
              </span>
            </div>
          )}
        </div>
        <div className="min-w-0">
          {open ? (
            <span
              className={cn(
                'inline-flex h-9 w-full items-center justify-between gap-1 rounded-md border px-3 text-sm font-semibold transition-colors',
                ACTION_TONE[tone],
              )}
              data-testid="queue-action"
              data-tone={tone}
            >
              {t(decision ? 'board.queue.decide' : 'board.queue.operate')}
              <ChevronRight aria-hidden className="h-4 w-4 shrink-0" />
            </span>
          ) : (
            <span
              className="inline-flex h-9 items-center gap-1 text-sm text-muted-foreground"
              data-testid="queue-action"
              data-tone="quiet"
            >
              {t('board.queue.view')}
              <ChevronRight aria-hidden className="h-4 w-4 shrink-0" />
            </span>
          )}
        </div>
      </Link>
      <Link
        to={towerPath(row.urn)}
        aria-label={t('board.queue.tower')}
        title={t('board.queue.tower')}
        data-testid={`queue-tower-${row.urn}`}
        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Radar aria-hidden className="h-4 w-4" />
      </Link>
    </li>
  );
}
